import { CancelOutlined, CheckCircleOutlined } from '@mui/icons-material';
import {
  Alert,
  Button,
  Snackbar,
  SnackbarCloseReason,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGridPremium, GridColDef } from '@mui/x-data-grid-premium';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiApplication } from '../../../../api/hexa/ApiApplication';

import { useNavigate } from 'react-router';
import ApplicationDetail from './ApplicationDetail';
import { Application } from '../../../../api/hexa/models/ApplicationModels';
import { Consumer } from '../../../../api/hexa/models/ConsumerModels';

interface ApplicationListProps {
  customerId: string;
}

const ApplicationList = ({ customerId }: ApplicationListProps) => {
  const [rows, setRows] = useState<Application[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState({
    open: false,
    message: '',
  });
  const navigate = useNavigate();
  const { t } = useTranslation();
  /**
   * Define the columns for the DataGrid
   */
  const columns = useMemo<GridColDef<Application>[]>(
    () => [
      { field: 'id', headerName: 'ID', flex: 1, maxWidth: 100 },
      { field: 'name', headerName: 'Name', flex: 2 },
      {
        field: 'consumers',
        headerName: 'Consumers',
        flex: 2,
        renderCell: (params) => (
          <Stack direction={'column'}>
            {params.value.map((consumer: Consumer) => (
              <Stack
                direction={'row'}
                spacing={1}
                key={`${consumer.id}_${Math.random()}`}
              >
                <Tooltip title={consumer.id}>
                  {consumer.active ? (
                    <CheckCircleOutlined color="success" />
                  ) : (
                    <CancelOutlined color="error" />
                  )}
                </Tooltip>
                <Typography key={consumer.id} variant={'body2'}>
                  {consumer.key}
                </Typography>
              </Stack>
            ))}
          </Stack>
        ),
      },
      { field: 'active', headerName: 'Active', flex: 1, type: 'boolean' },
      {
        field: 'created',
        headerName: 'Creation Date',
        type: 'date',
        flex: 1,
        valueGetter: (value, row) => (row.created ? new Date(row.created) : ''),
        renderCell: (params: any) =>
          new Date(params.row.created).toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          }),
      },
    ],
    []
  );

  /**
   * Fetch applications from the API and set the rows state
   */
  const fetchApplications = useCallback(async () => {
    setLoading(true);
    try {
      const applications = await ApiApplication.index(customerId);
      setRows(applications);
    } catch (error: any) {
      setError({
        open: true,
        message: `Error ${error.status ?? 500} fetching applications: 
        ${error.response?.data.error ?? 'Error fetching applications'}`,
      });
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  /**
   * Close the error snackbar
   */
  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setError({
      open: false,
      message: error.message,
    });
  };

  return (
    <>
      <Stack spacing={1}>
        <Typography variant={'h4'} sx={{ paddingY: '0.8rem' }}>
          {t('applications.title')}
        </Typography>
        <Stack
          direction="row"
          pb={'0.5rem'}
          pr={'0.5rem'}
          justifyContent="flex-end"
        >
          <Button variant="outlined" size="small" onClick={() => setOpen(true)}>
            {t('applications.create')}
          </Button>
        </Stack>
        <DataGridPremium
          key={Math.random()}
          getRowId={(row) => row.id}
          onRowClick={(params) => {
            navigate(`./applications/${params.row.id}`);
            window.scrollTo(0, 0);
          }}
          loading={loading}
          pagination={true}
          pageSizeOptions={[10, 25, 50, 100]}
          autoHeight={true}
          rows={rows}
          rowHeight={55}
          columns={columns}
          sx={{
            marginX: '0.5rem !important',
            '& .MuiDataGrid-cell[data-field="consumers"]': {
              paddingTop: '0.25rem',
            },
            '& .MuiDataGrid-cell': {
              overflowY: 'auto',
              display: 'flex',
              alignItems: 'flex-start',
            },
            '& .MuiDataGrid-cell--textCenter': {
              display: 'flex',
              alignItems: 'center',
            },
          }}
        />
        {open && (
          <ApplicationDetail
            open={open}
            onClose={() => setOpen(false)}
            applicationListUpdate={fetchApplications}
            isDialog={true}
          />
        )}
      </Stack>
      <Snackbar
        open={error.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="error">
          {error.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ApplicationList;
